import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TextField, FormControl, Box, Autocomplete, IconButton, CircularProgress } from '@mui/material';
import { debounce } from 'lodash';
import { AvailableModule } from '../../../types/types';
import APIService from '../../../services/APIService';
import { Visibility } from '@mui/icons-material';

interface ModulePickerProps {
  onModuleSelect: (module_id: string) => void;
  initialModuleId?: string;
  showUserModules?: boolean;
}

const ModulePicker: React.FC<ModulePickerProps> = ({
  onModuleSelect,
  initialModuleId,
  showUserModules = false,
}) => {
  const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<string | undefined>(initialModuleId);
  const [selectedModule, setSelectedModule] = useState<AvailableModule | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const hasSearched = useRef(false);

  useEffect(() => {
    if (initialModuleId && availableModules.length > 0) {
      const initialModule = availableModules.find(module => module.module_id === initialModuleId);
      if (initialModule) {
        setSelectedModule(initialModule);
      }
    }
  }, [initialModuleId, availableModules]);

  const fetchModules = useCallback(async (search: string = '') => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    setLoading(true);
    try {
      const response = await APIService.makeAPIGetRequest({
        requestString: `/template/getAllModules?search=${search}&user_modules=${showUserModules}`,
        accessToken: accessToken,
      });

      if (response.ok) {
        setAvailableModules(response.value.modules);
        hasSearched.current = true;
      }
    } catch (error) {
      console.error('Failed to fetch modules:', error);
      setAvailableModules([]);
    } finally {
      setLoading(false);
    }
  }, [showUserModules]);

  // Create debounced search function
  const debouncedFetchModules = useRef(
    debounce((search: string) => {
      fetchModules(search);
    }, 300)
  ).current;

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchModules.cancel();
    };
  }, [debouncedFetchModules]);

  const handleInputChange = (_event: React.ChangeEvent<{}>, value: string, reason: string) => {
    setInputValue(value);

    if (reason === 'input') {
      debouncedFetchModules.cancel();

      if (value.trim().length >= 2) {
        debouncedFetchModules(value.trim());
      } else if (value.trim().length === 0) {
        setAvailableModules([]);
        hasSearched.current = false;
      }
    }
  };

  const handleModuleChange = (_event: any, value: AvailableModule | null) => {
    if (value) {
      setSelectedModuleId(value.module_id);
      setSelectedModule(value);
      onModuleSelect(value.module_id);
    } else {
      setSelectedModuleId(undefined);
      setSelectedModule(null);
      onModuleSelect('');
    }
  };

  const handleOpen = () => {
    setOpen(true);
    // Fetch initial set if nothing searched yet
    if (!hasSearched.current && inputValue.length < 2) {
      fetchModules('');
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl fullWidth>
        <Autocomplete
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          options={availableModules}
          loading={loading}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          getOptionLabel={option => (option.name ? option.name : option.module_name)}
          renderInput={params => (
            <TextField
              {...params}
              label="Choose a module"
              variant="outlined"
              placeholder="Type at least 2 characters to search..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={handleModuleChange}
          isOptionEqualToValue={(option, value) => option.module_id === value.module_id}
          getOptionKey={option => option.module_id}
          value={selectedModule}
          noOptionsText={
            inputValue.trim().length < 2
              ? 'Type at least 2 characters to search'
              : loading
              ? 'Searching...'
              : 'No modules found'
          }
        />
      </FormControl>
      {selectedModuleId && (
        <IconButton
          aria-label="preview"
          onClick={() => {
            window.open(`${window.location.origin}/admin/module/${selectedModuleId}`, '_blank');
          }}
        >
          <Visibility />
        </IconButton>
      )}
    </Box>
  );
};

export default ModulePicker;
